import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Gift, Star, Mail, Tag } from "lucide-react";
import { LINKS, LOYALTY, TOAST_PROMO } from "@/content/site";
import { MaskLines, Reveal } from "@/components/site/motion";

const PointsCup = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 90%", "center 45%"] });
  const fill = useTransform(scrollYProgress, [0, 1], [250, 70]);
  const pts = useTransform(scrollYProgress, (v) => Math.round(Math.min(1, Math.max(0, v)) * 50));
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[300px]">
      <svg viewBox="0 0 200 300" className="w-full" role="img" aria-label="A cup filling up to 50 points">
        <defs><clipPath id="cupclip"><path d="M40 60 L160 60 L146 280 Q100 292 54 280 Z" /></clipPath></defs>
        <path d="M30 50 Q100 26 170 50 L166 62 L34 62 Z" fill="#1a0a10" />
        <rect x="114" y="0" width="12" height="70" rx="6" fill="#1a0a10" transform="rotate(14 120 35)" />
        <g clipPath="url(#cupclip)">
          <rect x="0" y="0" width="200" height="300" fill="#fff5e8" />
          <motion.rect x="0" width="200" height="300" fill="#ff2e63" style={{ y: fill }} />
          {[[70, 262], [94, 270], [120, 266], [82, 246], [108, 250], [132, 244]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="9" fill="#2a1309" />)}
        </g>
        <path d="M40 60 L160 60 L146 280 Q100 292 54 280 Z" fill="none" stroke="#1a0a10" strokeWidth="6" />
      </svg>
      <div className="absolute inset-x-0 top-[44%] text-center">
        <motion.span className="font-display text-6xl font-extrabold text-[#1a0a10]">{pts}</motion.span>
        <span className="block font-display text-xs font-bold uppercase tracking-widest text-[#1a0a10]">points</span>
      </div>
    </div>
  );
};

const Tile = ({ icon: Icon, title, text, href, cta, testId, className }) => (
  <Reveal className={`flex flex-col rounded-[2rem] p-7 ${className}`}>
    <Icon className="h-7 w-7" aria-hidden />
    <h3 className="mt-6 font-display text-2xl font-extrabold leading-tight">{title}</h3>
    <p className="mt-2 flex-1 text-[15px] opacity-85">{text}</p>
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider underline underline-offset-4">{cta}<ArrowUpRight className="h-4 w-4" /></a>
  </Reveal>
);

export const Rewards = () => (
  <section data-testid="rewards-section" className="grain relative overflow-hidden bg-[#ffd23f] py-24 text-[#1a0a10] sm:py-32">
    <div className="wrap grid items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <p className="eyebrow">TeaDM Rewards</p>
        <MaskLines lines={[`Every $${LOYALTY.spend} spent`, `= ${LOYALTY.points} points`, `= ${LOYALTY.reward}.`]} className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
        <p className="mt-6 max-w-md text-lg">Earn points for free drinks at every TeaDM. Look up your rewards or join the program on Toast.</p>
        <a href={LINKS.rewards} target="_blank" rel="noopener noreferrer" data-testid="rewards-join-btn" className="btn btn-ink mt-8"><Star className="h-4 w-4" />Join our loyalty program</a>
      </div>
      <div className="lg:col-span-5"><PointsCup /></div>
    </div>
    <div className="wrap mt-16 grid gap-4 md:grid-cols-3">
      <Tile icon={Gift} title="Gift cards" text="Valid at all TeaDM locations — the easiest gift for any boba lover." href={LINKS.giftCards} cta="Buy a gift card" testId="rewards-gift-card-link" className="bg-[#ff2e63] text-white" />
      <Tile icon={Tag} title={`${TOAST_PROMO.title} · ${TOAST_PROMO.code}`} text={TOAST_PROMO.note} href={LINKS.toastHub} cta="Order on Toast" testId="rewards-promo-link" className="bg-[#1a0a10] text-[#fff5e8]" />
      <Tile icon={Mail} title="Toast newsletter" text="Get TeaDM news and offers straight from our ordering system." href={LINKS.toastNewsletter} cta="Subscribe on Toast" testId="rewards-toast-newsletter-link" className="bg-[#fff5e8]" />
    </div>
  </section>
);
